<template>
  <list-page-shell>
    <template #actions>
      <div class="template-list-actions">
        <ContractMergePlaceholderDialog
          :button-text="$t('templateListPage.placeholderHelp')"
          button-type="default"
          button-size="small"
          :plain="true"
        />
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openTplForm(null)">
          {{ $t('common.create') }}
        </el-button>
      </div>
    </template>

    <template #filters>
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item :label="$t('templateListPage.keywordLabel')">
          <el-input
            v-model="filters.keyword"
            clearable
            :placeholder="$t('templateListPage.keywordPh')"
            style="width:220px;"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item :label="$t('common.status')">
          <el-select v-model="filters.templateStatusCode" clearable :placeholder="$t('common.pleaseSelect')" style="width:120px;">
            <el-option v-for="o in templateStatusOptions" :key="o.value" :value="o.value" :label="o.label" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" :disabled="loading" @click="handleReset">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </template>

    <el-table :data="list" border stripe v-loading="loading" style="width:100%;">
      <el-table-column prop="templateName" :label="$t('grid.name')" min-width="160" show-overflow-tooltip />
      <el-table-column prop="versionTag" :label="$t('grid.version')" width="100" />
      <el-table-column :label="$t('common.status')" min-width="88">
        <template slot-scope="s">{{ labelTemplateStatus(s.row.templateStatusCode) }}</template>
      </el-table-column>
      <el-table-column :label="$t('common.actions')" width="220" fixed="right">
        <template slot-scope="s">
          <el-button type="text" size="small" @click="openTplForm(s.row.id)">{{ $t('common.edit') }}</el-button>
          <el-button type="text" size="small" class="danger-text" @click="remove(s.row)">{{ $t('common.delete') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="list-pager">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :page-sizes="[10, 20, 50]"
        :total="total"
        :page-size="pageSize"
        :current-page="pageNo"
        @size-change="onSizeChange"
        @current-change="handlePageChange"
      />
    </div>
    <RecordFormDialog :visible.sync="tplFormVisible" :title="tplFormTitle">
      <ContractTemplateForm
        v-if="tplFormVisible"
        ref="tplFormRef"
        :key="tplFormKey"
        embedded
        :record-id="tplFormRecordId"
        @saved="onTplFormSaved"
        @saving="v => (tplFormSaving = v)"
      />
      <template slot="footer">
        <el-button @click="tplFormVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="tplFormSaving" @click="submitTplFormDialog">{{ $t('contractTemplateFormPage.save') }}</el-button>
      </template>
    </RecordFormDialog>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import RecordFormDialog from '@/components/RecordFormDialog.vue'
import ContractTemplateForm from '@/views/system/ContractTemplateForm.vue'
import ContractMergePlaceholderDialog from '@/components/ContractMergePlaceholderDialog.vue'
import { deleteTemplate, getTemplatePage } from '@/api/contractTemplate'
import { enumSelectOptions, labelTemplateStatus } from '@/utils/crmLabels'

export default {
  name: 'ContractTemplateList',
  components: { ListPageShell, RecordFormDialog, ContractTemplateForm, ContractMergePlaceholderDialog },
  data() {
    return {
      tplFormVisible: false,
      tplFormSaving: false,
      tplFormRecordId: null,
      tplFormKey: 0,
      list: [],
      total: 0,
      pageNo: 1,
      pageSize: 10,
      loading: false,
      filters: { keyword: '', templateStatusCode: null }
    }
  },
  computed: {
    tplFormTitle() {
      return this.tplFormRecordId
        ? this.$t('contractTemplateFormPage.editTitle')
        : this.$t('contractTemplateFormPage.newTitle')
    },
    templateStatusOptions() {
      void this.$locale
      return enumSelectOptions(this.$t, 'templateStatus', [1, 2])
    }
  },
  created() {
    this.load()
  },
  methods: {
    labelTemplateStatus,
    openTplForm(id) {
      this.tplFormRecordId = id != null ? id : null
      this.tplFormKey += 1
      this.tplFormVisible = true
    },
    onTplFormSaved() {
      this.tplFormVisible = false
      this.load()
    },
    submitTplFormDialog() {
      if (this.$refs.tplFormRef) this.$refs.tplFormRef.submit()
    },
    handlePageChange(p) {
      this.pageNo = p
      this.load()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.pageNo = 1
      this.load()
    },
    handleSearch() {
      this.pageNo = 1
      this.load()
    },
    handleReset() {
      this.filters = { keyword: '', templateStatusCode: null }
      this.pageNo = 1
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const params = { pageNo: this.pageNo, pageSize: this.pageSize }
        const kw = (this.filters.keyword || '').trim()
        if (kw) params.keyword = kw
        if (this.filters.templateStatusCode != null && this.filters.templateStatusCode !== '') {
          params.templateStatusCode = this.filters.templateStatusCode
        }
        const r = await getTemplatePage(params)
        const d = r.data || r
        this.list = d.records || []
        this.total = d.total || 0
      } finally {
        this.loading = false
      }
    },
    remove(row) {
      const name = row.templateName || row.id
      this.$confirm(this.$t('templateListPage.confirmDelete', { name }), this.$t('common.tip'), {
        type: 'warning',
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel')
      })
        .then(async () => {
          await deleteTemplate(row.id)
          this.$message.success(this.$t('common.deleted'))
          this.load()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.template-list-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.danger-text {
  color: #f87171;
}

.list-pager {
  margin-top: 16px;
  text-align: right;
}
</style>
